/**
 * Colours for the plan Excel export (ag-Grid `excelStyles`).
 *
 * A spreadsheet cannot read CSS custom properties, so these are literal copies
 * of the Lyra token values (`@niceltd/sol-angular` sol-tokens.scss). Each
 * entry names the token it mirrors - update both together.
 *
 * Format: `#RRGGBB`, which ag-Grid's Excel writer converts to the ARGB
 * (`FFRRGGBB`, opaque) value the xlsx stores. Lyra tokens with an alpha
 * channel are flattened onto the white cell background.
 */
export const EXCEL_COLORS = {
  /** Calculated fields (font) - `--lyra-color-accent-blue-strong` (`--lyra-base-color-blue-500`, rgb(49 99 201)). */
  calculatedText: '#3163C9',
  /** Capped fields (font) - `--lyra-color-accent-orange-strong` (`--lyra-base-color-orange-500`, rgb(174 83 0)). */
  cappedText: '#AE5300',
  /** Negative values (font) - `--lyra-color-status-critical-strong` / `--sol-color-text-status-critical` (`--lyra-base-color-red-500`, rgb(201 50 50)). */
  negativeText: '#C93232',
  /** Column header fill - `--lyra-color-accent-slate-subtle-bg` (`--lyra-base-color-slate-50`, rgb(243 245 247)). */
  headerFill: '#F3F5F7',
  /** Column header bottom border - `--lyra-color-border-subtle` (rgb(0 0 0 / 10%) on white). */
  headerBorder: '#E6E6E6',
  /** Column group header fill - `--lyra-base-color-slate-100` (rgb(233 237 241)). */
  headerGroupFill: '#E9EDF1',
  /** Editable cell fill - `--lyra-color-bg-surface-container` (`--lyra-base-color-white`). */
  editableFill: '#FFFFFF',
  /** Read-only cell fill - `--lyra-color-accent-slate-subtle-bg` (`--lyra-base-color-slate-50`, rgb(243 245 247)). */
  readonlyFill: '#F3F5F7',
  /** Closed-day hatching stripes - `--lyra-base-color-slate-300` (rgb(172 183 195)). */
  closedStripe: '#ACB7C3',
  /** Closed-day hatching background - `--lyra-base-color-white`. */
  closedStripeBackground: '#FFFFFF',
  /**
   * Locked cells (font) - `--sol-color-text-secondary` / `--lyra-color-fg-secondary`
   * (rgb(0 0 0 / 60%) on white). Greyed like the on-screen locked cell, but kept
   * readable (5.7:1) since the exported values must still be legible;
   * `--sol-color-text-disabled` (30%) would be ~2:1.
   */
  lockedText: '#666666',
  /** Metadata (protected) header text - `--lyra-base-color-blue-700` (rgb(31 59 117)). */
  protectedHeaderText: '#1F3B75',
  /** Metadata (protected) header fill - `--lyra-color-accent-blue-subtle-bg` (`--lyra-base-color-blue-50`, rgb(229 238 255)). */
  protectedHeaderFill: '#E5EEFF'
} as const;

export type ExcelColor = typeof EXCEL_COLORS[keyof typeof EXCEL_COLORS];
